/**
 * Configuration des étapes du tutoriel interactif de création de contrat
 *
 * L'ordre suit le flow réel du wizard :
 *   Step 1 (Bénéficiaire) → Step 2 (Véhicule) → Step 3 (Garantie)
 */

import { ContractFormState } from '@/features/contracts/hooks/useContractFormWizard';
import { getVendorWording, type VendorWording } from '@/utils/vendorWording';

export interface TutorialStep {
  id: string;
  fieldRef: string | string[] | ((formData: ContractFormState) => string | string[] | null); // ID du champ dans le DOM (ou fonction pour dériver dynamiquement)
  title: string;
  content: string;
  validation: (formData: ContractFormState) => boolean;
  wizardStep: number; // Numéro de l'étape du wizard
  fieldType: 'text' | 'select' | 'radio' | 'number'; // Type de champ pour ajuster le délai d'auto-progression
  placement?: 'auto' | 'top'; // Position du tooltip (défaut: 'auto')
}

/**
 * Génère les étapes du tutoriel avec le wording adapté au vendorType (garantie/prestation)
 */
function buildTutorialSteps(wording: VendorWording): TutorialStep[] {
  return [
  // ========== Step 1 - Bénéficiaire ==========
  {
    id: 'society',
    fieldRef: 'society-user-block',
    title: 'Société et utilisateur',
    content: 'Choisissez la société pour laquelle vous créez ce contrat, puis sélectionnez l\'utilisateur associé.',
    validation: (formData) => !!formData.societyId && !!formData.userId,
    wizardStep: 1,
    fieldType: 'select',
  },
  {
    id: 'beneficiary-identity',
    fieldRef: 'beneficiary-identity-block',
    title: 'Identité du bénéficiaire',
    content: 'Renseignez les informations d\'identité du bénéficiaire : prénom, nom et société (optionnel). Ces informations apparaîtront sur le contrat.',
    validation: (formData) =>
      !!formData.beneficiary.firstName &&
      formData.beneficiary.firstName.length >= 2 &&
      !!formData.beneficiary.lastName &&
      formData.beneficiary.lastName.length >= 2,
    wizardStep: 1,
    fieldType: 'text',
  },
  {
    id: 'beneficiary-contact',
    fieldRef: 'beneficiary-contact-block',
    title: 'Contact du bénéficiaire',
    content: 'Saisissez les coordonnées de contact du bénéficiaire : email et téléphone (tous deux obligatoires). Ces informations seront utilisées pour les communications.',
    validation: (formData) => !!formData.beneficiary.email && formData.beneficiary.email.includes('@') && !!formData.beneficiary.phoneNumber,
    wizardStep: 1,
    fieldType: 'text',
  },
  {
    id: 'beneficiary-address',
    fieldRef: 'beneficiary-address-block',
    title: 'Adresse du bénéficiaire',
    content: 'Renseignez l\'adresse complète du bénéficiaire. Utilisez l\'autocomplétion pour faciliter la saisie, puis complétez si nécessaire avec le code postal et la ville.',
    validation: () => true, // Optionnel
    wizardStep: 1,
    fieldType: 'text',
  },

  // ========== Step 2 - Véhicule ==========
  {
    id: 'vehicle-info',
    fieldRef: 'vehicle-search-card',
    title: 'Informations du véhicule',
    content: 'Entrez l\'immatriculation ou le VIN pour rechercher automatiquement le véhicule, ou cliquez sur "Saisir manuellement" pour remplir les champs. Complétez toutes les informations obligatoires.',
    validation: (formData) =>
      !!formData.vehicle.registration &&
      !!formData.vehicle.serialNumber &&
      !!formData.vehicle.brand &&
      !!formData.vehicle.model &&
      !!formData.vehicle.entryIntoService,
    wizardStep: 2,
    fieldType: 'text',
  },
  {
    id: 'vehicle-pricing',
    fieldRef: 'vehicle-pricing-block',
    title: 'Prix de vente et kilométrage',
    content: 'Saisissez le prix de vente TTC du véhicule ainsi que le kilométrage actuel.',
    validation: (formData) => formData.vehicleSellingPrice !== undefined && formData.vehicleSellingPrice > 0 && formData.kmStart !== undefined && formData.kmStart > 0,
    wizardStep: 2,
    fieldType: 'number',
  },

  // ========== Step 3 - Garantie/Prestation & Configuration ==========
  {
    id: 'warranty',
    fieldRef: 'warranty-select',
    title: `Sélectionnez ${wording.withIndefinite}`,
    content: `Parcourez les ${wording.pluralLower} disponibles dans la grille ci-dessous et cliquez sur une card pour la sélectionner. Vous pouvez utiliser les filtres en haut (type de véhicule, durée) pour affiner votre recherche. Vous pouvez également créer une ${wording.singularLower} personnalisée en cliquant sur le bouton dédié.`,
    validation: (formData) => !!formData.warrantyUuid,
    wizardStep: 3,
    fieldType: 'select',
    placement: 'top',
  },
  {
    id: 'payment-mode',
    fieldRef: 'payment-mode-block',
    title: 'Mode de paiement et prix',
    content: `Choisissez le mode de paiement (comptant ou mensualisé) et renseignez le prix de vente TTC. Certaines ${wording.pluralLower} n'autorisent qu'un seul mode de paiement.`,
    validation: (formData) => (formData.paymentType === 1 || formData.paymentType === 2) && formData.sellingPrice >= 0,
    wizardStep: 3,
    fieldType: 'select',
  },
  {
    id: 'subscription-date',
    fieldRef: 'subscription-date-input',
    title: 'Date de souscription',
    content: 'Sélectionnez la date à laquelle le contrat prend effet. La date apparaîtra dans l\'aperçu PDF.',
    validation: (formData) => !!formData.subscriptionDate,
    wizardStep: 3,
    fieldType: 'text',
  },
  ];
}

/**
 * Obtient les étapes du tutoriel filtrées selon le type d'utilisateur
 */
export function getFilteredTutorialSteps(
  isExternalUser = false,
  vendorType?: number,
): TutorialStep[] {
  const wording = getVendorWording(vendorType);
  let steps = buildTutorialSteps(wording);

  // Filtrer l'étape société/utilisateur si utilisateur externe
  // (ces champs sont pré-remplis automatiquement)
  if (isExternalUser) {
    steps = steps.filter(step => step.id !== 'society');
  }

  return steps;
}
